<script setup>
import ProfileAvatar from "./common/ProfileAvatar.vue";

const config = useRuntimeConfig();

const sidebarVisible = ref(false);
const profileVisible = ref(false);
const navbarHeight = ref(64);
const loginWithGoogle = () => {
  navigateTo(config.public.apiBaseUrl + "/auth/google/redirect", {
    external: true,
  });
};

const items = ref([
  {
    label: "Explore",
    to: "/explore",
  },
  {
    label: "Universities",
    to: "/universities",
  },
  {
    label: "Scholarships",
    to: "/scholarships",
  },
  {
    label: "Ambassadors",
    to: "/ambassador",
  },
  {
    label: "Webinars",
    to: "/webinars",
  },
  {
    label: "Blogs",
    to: "/blogs",
  },
  {
    label: "My Programs",
    to: "/profile/applications",
  },
  // {
  //   label: "Contact us",
  //   to: "/contact-us",
  // },
  // {
  //   label: "Invite friend",
  //   to: "/invite",
  // },
]);

const profileItems = ref([
  {
    label: "Applications",
    to: "/profile/applications",
    icon: "pi pi-briefcase",
  },
  { label: "Overview", to: "/profile", icon: "pi pi-cog" },
  { label: "My account", to: "/profile/my-account", icon: "pi pi-user" },
  { label: "Meeting", to: "/profile/meeting", icon: "pi pi-video" },
  { label: "Webinar", to: "/profile/webinar", icon: "pi pi-calendar" },
  {
    label: "Invite Friend",
    to: "/profile/referral-bonus",
    icon: "pi pi-gift",
  },
  { label: "Logout", icon: "pi pi-sign-out" },
]);

const wishlistVisible = ref(false);
const op = ref();
const wishlists = ref([]);

const { signOut, data: sessionData, status } = useAuth();

const authUser = computed(() => {
  return sessionData.value?.data;
});

const profileImage = computed(() => {
  return sessionData.value?.data?.profile?.media?.at(0)?.original_url;
});

async function handleLogout() {
  localStorage.clear(); // Clear all data from localStorage
  sessionStorage.clear(); // Clear all data from sessionStoragecom
  await signOut({ callbackUrl: "/signin", redirect: true });
}

const toggle = (event) => {
  op.value.toggle(event);
};

const openWishlist = () => {
  if (status.value !== "authenticated") return;
  wishlistVisible.value = true;
};

const goTo = (path) => {
  if (status.value !== "authenticated") return;
  navigateTo(path);
};
</script>

<template>
  <section class="sticky top-0 z-40">
    <!-- desktop -->
    <div class="hidden shadow bg-white lg:block">
      <div
        class="flex gap-8 flex-row-reverse text-white container py-1 min-h-[40px]"
        style="background-color: #143257"
      >
        <div class="items-center flex gap-2">
          <Comparision />
          <ProfileCompleteGuard @on-callback="openWishlist">
            <Button
              label="Wishlist"
              text
              :icon="
                authUser?.wishlists?.length > 0
                  ? 'pi pi-heart-fill'
                  : 'pi pi-heart'
              "
              class="text-white"
              size="small"
              severity="secondary"
              :badge="authUser?.wishlists?.length"
              @click="openWishlist"
            />
          </ProfileCompleteGuard>
          <template v-if="status !== 'unauthenticated'">
            <Button
              text
              size="small"
              class="!p-0 hover:bg-transparent text-white"
              @click="toggle"
            >
              <ProfileAvatar
                :name="authUser?.name"
                :imageSrc="profileImage"
                size="sm"
                type="button"
              />
            </Button>
            <Popover
              ref="op"
              :pt="{
                content: 'px-3 py-3 rounded-lg border border-slate-200',
              }"
            >
              <div class="flex flex-col gap-1">
                <Button
                  text
                  size="small"
                  icon="pi pi-user"
                  label="Profile"
                  class="px-4"
                  @click="navigateTo('/profile')"
                >
                </Button>
                <Button
                  severity="danger"
                  text
                  size="small"
                  icon="pi pi-sign-out"
                  label="Logout"
                  @click="handleLogout"
                  class="px-4"
                >
                </Button>
              </div>
            </Popover>
          </template>
          <template v-else>
            <Button
              label="Login"
              text
              icon="pi pi-user"
              size="small"
              class="text-white"
              @click="navigateTo('/signin')"
            />
            <Button
              label="Sign up"
              text
              icon="pi pi-sign-in"
              size="small"
              class="text-white"
              @click="navigateTo('/signup')"
            />
          </template>
          <ProfileCompleteGuard @on-callback="goTo('/invite')">
            <Button
              severity="secondary"
              size="small"
              rounded
              class="ml-8 px-3 py-1"
              @click="goTo('/invite')"
            >
              <img
                src="~/assets/img/icon/gift.svg"
                alt="gift"
                class="size-4 mr-2"
              />
              <span class="text-primary font-bold">Invite Friend</span>
            </Button>
          </ProfileCompleteGuard>
          <Notification v-if="status !== 'unauthenticated'" />
        </div>
      </div>

      <div class="flex gap-4 justify-between items-center px-4 lg:px-10">
        <div class="shrink-0">
          <img
            src="~/assets/img/logo-primary-new.svg"
            alt="Unilinks"
            class="mr-10 cursor-pointer"
            @click="navigateTo('/')"
            width="130"
          />
        </div>
        <Menubar
          :model="items"
          :pt="{
            item: 'leading-[4] px-3',
            itemLabel: 'text-sm font-bold text-white tracking-wide ',
            itemContent: 'hover:bg-transparent hover:text-active',
          }"
          class="border-0 py-0"
        >
          <template #item="{ item, props }">
            <ProfileCompleteGuard
              v-if="['/profile/applications', '/invite'].includes(item.to)"
              :isOnlyRequireLogin="item.to === '/profile/applications'"
              @on-callback="goTo(item.to)"
            >
              <div
                @click="goTo(item.to)"
                class="w-full inline-block border-b-2 border-transparent header-menu cursor-pointer hover:border-blue-500"
                :class="{
                  'border-blue-500 text-blue-500': $route.path.startsWith(
                    item.to,
                  ),
                }"
              >
                <span>{{ item.label }}</span>
              </div>
            </ProfileCompleteGuard>
            <NuxtLink
              v-else-if="item.to"
              :to="item.to"
              class="w-full inline-block border-b-2 border-transparent header-menu hover:border-blue-500"
            >
              <span>{{ item.label }}</span>
            </NuxtLink>
          </template>
        </Menubar>

        <SearchExplore />
      </div>
    </div>

    <!-- mobile -->
    <div class="block lg:hidden bg-white shadow">
      <div
        class="flex items-center justify-between px-4 py-2"
        style="background-color: #143257"
      >
        <figure>
          <img
            src="~/assets/img/logo-sec.svg"
            alt="Unilinks"
            class="w-32 cursor-pointer"
            @click="navigateTo('/')"
          />
        </figure>

        <div class="flex items-center gap-x-6 text-white">
          <div class="mt-2">
            <Notification v-if="status === 'authenticated'" />
          </div>

          <div class="flex items-center">
            <template v-if="status !== 'unauthenticated'">
              <ProfileAvatar
                :name="authUser?.name?.split(' ')?.[0]"
                :imageSrc="profileImage"
                size="sm"
                type="button"
                class="cursor-pointer"
                @click="profileVisible = true"
              />
            </template>
            <template v-else>
              <i
                class="pi pi-user text-lg cursor-pointer"
                @click="
                  navigateTo('/signin');
                  sidebarVisible = false;
                "
              ></i>
            </template>
          </div>

          <div class="flex items-center">
            <i
              class="pi pi-bars text-xl cursor-pointer"
              @click="sidebarVisible = true"
            ></i>
          </div>
        </div>
      </div>
      <div v-if="$route.path !== '/'" class="px-4 py-0 pb-0.5 search-explore">
        <SearchExplore />
      </div>
    </div>

    <!-- profile selection -->
    <Drawer
      v-model:visible="profileVisible"
      :header="status === 'authenticated' ? 'Profile' : 'Login'"
      position="top"
      class="custom-drawer fixed right-0 bg-white shadow-lg transition-all duration-300"
      :style="{
        top: navbarHeight + 'px',
      }"
    >
      <div>
        <div v-if="status === 'authenticated'">
          <div>
            <ProfileAvatar
              :name="authUser?.name"
              :imageSrc="profileImage"
              size="md"
              type="button"
              @click="navigateTo('/profile')"
            />
          </div>
          <div class="pt-6 flex flex-col justify-center gap-y-3">
            <ProfileCompleteGuard @on-callback="openWishlist">
              <div
                class="flex items-center cursor-pointer relative"
                @click="openWishlist"
              >
                <i
                  class="cursor-pointer text-lg"
                  :class="
                    authUser?.wishlists?.length > 0
                      ? 'pi pi-heart-fill'
                      : 'pi pi-heart'
                  "
                  @click="openWishlist"
                ></i>
                <span class="ml-2.5">Wishlist</span>
                <span
                  v-if="authUser?.wishlists?.length"
                  class="absolute -top-1 left-[13px] bg-red-500 text-white text-[10px] md:text-xs font-bold rounded-full px-1"
                >
                  {{ authUser?.wishlists?.length }}
                </span>
              </div>
            </ProfileCompleteGuard>
            <Comparision />
            <NuxtLink
              v-for="(item, index) in profileItems"
              :key="index"
              :to="item.label === 'Logout' ? '#' : item.to"
              class="w-fit hover:text-blue-500"
              :class="{
                'text-blue-500 font-semibold':
                  item.to === '/profile'
                    ? $route.path === '/profile'
                    : $route.path.startsWith(item.to),
              }"
              @click.prevent="
                () => {
                  sidebarVisible = false;
                  item.label === 'Logout'
                    ? handleLogout()
                    : navigateTo(item.to);
                }
              "
            >
              <i :class="item.icon" />
              <span class="ml-2.5">{{ item.label }}</span>
            </NuxtLink>
          </div>
        </div>
        <div v-else class="space-y-6">
          <Button
            label="Login with Google"
            severity="secondary"
            icon="pi pi-google"
            outlined
            class="w-full text-sm lg:text-lg !border !border-[#64748B]"
            @click.prevent="loginWithGoogle"
          >
            <template #icon>
              <img src="~/assets/img/icon/google.svg" />
            </template>
          </Button>
          <Button
            label="Continue with email"
            severity="secondary"
            icon="pi pi-envelope"
            outlined
            class="w-full text-sm py-3 lg:text-lg !border !border-[#64748B]"
            @click="navigateTo('/signin')"
          >
          </Button>
        </div>
      </div>
    </Drawer>

    <!-- drawer sidebar -->
    <Drawer
      v-model:visible="sidebarVisible"
      header="Navigation"
      position="right"
      class="w-full"
    >
      <div class="flex flex-col gap-4">
        <NuxtLink
          v-for="(item, index) in items"
          :key="index"
          :to="item.to"
          class="w-fit hover:text-blue-500"
          :class="{
            'text-blue-500 font-semibold': $route.path.startsWith(item.to),
          }"
          @click.prevent="
            () => {
              sidebarVisible = false;
              navigateTo(item.to);
            }
          "
        >
          {{ item.label }}
        </NuxtLink>
      </div>
    </Drawer>

    <!-- wishlists sidebar -->
    <Drawer
      v-model:visible="wishlistVisible"
      header="All wishlists"
      position="right"
      class="w-full lg:w-1/2"
    >
      <AllWishlist />
    </Drawer>
  </section>
</template>
